import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { generateDealershipSlug } from '../../../services/dealershipService';
import { partnerCategoryService } from '../../../services/partnerCategoryService';
import { geocodingService } from '../../../services/geocodingService';
import { 
  validateField, 
  dealershipValidationSchema,
  sanitizeInput,
  sanitizeDealershipName,
  sanitizeAddress,
  formatPhoneNumber
} from '../../../utils/validation';

const DealershipForm = ({ dealership, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: dealership?.name || '',
    city: dealership?.city || '',
    street: dealership?.street || '',
    postalCode: dealership?.postalCode || '',
    email: dealership?.email || '',
    contactEmail: dealership?.contactEmail || '',
    phone: dealership?.phone || '',
    contactPerson: dealership?.contactPerson || '',
    contactPhone: dealership?.contactPhone || '',
    website: dealership?.website || '',
    logo: dealership?.logo || '',
    logoAlt: dealership?.logoAlt || '',
    ciPrimaryColor: dealership?.ciPrimaryColor || '',
    ciSecondaryColor: dealership?.ciSecondaryColor || '',
    logoBackgroundColor: dealership?.logoBackgroundColor || '#FFFFFF',
    voucherValue: dealership?.voucherValue || 150,
    voucherCurrency: dealership?.voucherCurrency || 'Euro',
    premium2026: dealership?.premium2026 ?? 150,
    premium2027: dealership?.premium2027 ?? 0,
    partnerCategoryId: dealership?.partnerCategoryId || '',
    showInPartnerFinder: dealership?.showInPartnerFinder || false,
    latitude: dealership?.latitude || null,
    longitude: dealership?.longitude || null
  });
  const [logoPreview, setLogoPreview] = useState(dealership?.logo || '');
  const [generatedUrl, setGeneratedUrl] = useState(dealership?.generatedUrl || '');
  const [isExtracting, setIsExtracting] = useState(false);
  const [isGeocodingAddress, setIsGeocodingAddress] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (formData?.name && formData?.city) {
      const slug = generateDealershipSlug(formData?.name, formData?.city);
      setGeneratedUrl(slug);
    }
  }, [formData?.name, formData?.city]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await partnerCategoryService?.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleGeocodeAddress = async () => {
    if (!formData?.postalCode || !formData?.city) {
      setErrors(prev => ({ ...prev, geocoding: 'Bitte geben Sie zuerst Postleitzahl und Stadt ein' }));
      return;
    }

    setIsGeocodingAddress(true);
    setErrors(prev => ({ ...prev, geocoding: '' }));

    try {
      const result = await geocodingService?.geocodeAddress({
        street: formData?.street,
        postalCode: formData?.postalCode,
        city: formData?.city
      });

      setFormData(prev => ({
        ...prev,
        latitude: result?.latitude,
        longitude: result?.longitude
      }));

      // Show success message
      setErrors(prev => ({ 
        ...prev, 
        geocoding: `Koordinaten erfolgreich ermittelt: ${result?.latitude?.toFixed(6)}, ${result?.longitude?.toFixed(6)}` 
      }));
    } catch (error) {
      console.error('Geocoding error:', error);
      setErrors(prev => ({ ...prev, geocoding: error?.message || 'Fehler beim Ermitteln der Koordinaten. Bitte versuchen Sie es erneut.' }));
    } finally {
      setIsGeocodingAddress(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;

    // For the name field, preserve spaces during typing
    // For the street field, use sanitizeAddress to explicitly preserve spaces
    // For other fields, apply standard sanitization
    let processedValue;
    if (name === 'name') {
      processedValue = sanitizeDealershipName(value);
    } else if (name === 'street') {
      processedValue = sanitizeAddress(value);
    } else {
      processedValue = sanitizeInput(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    // Real-time validation
    if (touched?.[name]) {
      const rules = dealershipValidationSchema?.[name];
      if (rules) {
        const error = validateField(name, processedValue, rules);
        setErrors(prev => ({ 
          ...prev, 
          [name]: error 
        }));
      }
    }
  };

  const handleBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    
    // Validate on blur
    const rules = dealershipValidationSchema?.[fieldName];
    if (rules) {
      const error = validateField(fieldName, formData?.[fieldName], rules);
      setErrors(prev => ({ 
        ...prev, 
        [fieldName]: error 
      }));
    }
    
    // Format phone number on blur
    if (fieldName === 'phone' && formData?.phone) {
      const formatted = formatPhoneNumber(formData?.phone);
      setFormData(prev => ({ ...prev, phone: formatted }));
    }
  };

  const handleColorChange = (e) => {
    const { name, value } = e?.target;
    let sanitized = value?.trim()?.toUpperCase();
    
    // Auto-add # if missing
    if (sanitized && !sanitized?.startsWith('#')) {
      sanitized = '#' + sanitized;
    }
    
    setFormData(prev => ({ ...prev, [name]: sanitized }));
    
    // Real-time validation for hex color
    if (touched?.[name]) {
      const rules = dealershipValidationSchema?.[name];
      if (rules) {
        const error = validateField(name, sanitized, rules);
        setErrors(prev => ({ 
          ...prev, 
          [name]: error 
        }));
      }
    }
  };

  const handleLogoUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.type?.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader?.result);
          setFormData(prev => ({ 
            ...prev, 
            logo: reader?.result,
            logoAlt: `Logo des Partners ${formData?.name} in ${formData?.city}`
          }));
        };
        reader?.readAsDataURL(file);
      } else {
        setErrors(prev => ({ ...prev, logo: 'Bitte wählen Sie eine Bilddatei aus' }));
      }
    }
  };

  const handleExtractCI = async () => {
    if (!formData?.website) {
      setErrors(prev => ({ ...prev, website: 'Bitte geben Sie eine Website-URL ein' }));
      return;
    }

    setIsExtracting(true);
    setErrors(prev => ({ ...prev, website: '' }));
    
    try {
      // Validate URL format
      const url = new URL(formData?.website);
      
      // Note: This is a placeholder for CI color extraction
      // In production, you would:
      // 1. Use a web scraping API or service
      // 2. Call a backend endpoint that extracts colors from the website
      // 3. Use a third-party color extraction service
      
      // For now, we show a clear message that manual input is needed
      alert(
        'CI-Farben-Extraktion erfordert manuelle Eingabe.\n\n' +
        'Bitte öffnen Sie die Website des Partners und kopieren Sie die Hauptfarben aus deren Corporate Design in die entsprechenden Felder.\n\n'+ 'Tipp: Verwenden Sie Browser-Entwicklertools (F12) oder Online-Farbwähler um die exakten Hex-Codes zu ermitteln.'
      );
      
    } catch (error) {
      console.error('CI extraction error:', error);
      setErrors(prev => ({ ...prev, website: 'Ungültige URL. Bitte Format https://www.beispiel.de verwenden' }));
    } finally {
      setIsExtracting(false);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      handleLogoUpload({ target: { files: [file] } });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    Object.keys(dealershipValidationSchema)?.forEach(fieldName => {
      const rules = dealershipValidationSchema?.[fieldName];
      const error = validateField(fieldName, formData?.[fieldName], rules);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    setTouched(Object.keys(dealershipValidationSchema)?.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {}));
    
    return isValid;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave({
        ...formData,
        name: formData?.name?.trim(), // Trim name before saving
        generatedUrl,
        id: dealership?.id || Date.now()
      });
    }
  };

  // Category options for select
  const categoryOptions = [
    { value: '', label: 'Keine Kategorie' },
    ...categories?.map(cat => ({
      value: cat?.id,
      label: cat?.name
    }))
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <Input
          label="Partnername"
          type="text"
          name="name"
          value={formData?.name}
          onChange={handleInputChange}
          onBlur={() => handleBlur('name')}
          placeholder="z.B. Partner Müller"
          error={touched?.name ? errors?.name : ''}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Stadt"
            type="text"
            name="city"
            value={formData?.city}
            onChange={handleInputChange}
            onBlur={() => handleBlur('city')}
            placeholder="z.B. München"
            error={touched?.city ? errors?.city : ''}
            required
          />
          
          <Input
            label="Postleitzahl"
            type="text"
            name="postalCode"
            value={formData?.postalCode}
            onChange={handleInputChange}
            onBlur={() => handleBlur('postalCode')}
            leftIcon="MapPin"
            placeholder="z.B. 80331"
            maxLength={5}
          />
        </div>

        {/* Coordinates Section */}
        <div className="col-span-2">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Map" size={20} />
            Kartenkoordinaten
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Koordinaten werden automatisch aus der Adresse ermittelt. Sie können diese auch manuell eingeben oder korrigieren.
          </p>
          
          {/* Geocoding Button */}
          <div className="mb-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleGeocodeAddress}
              disabled={isGeocodingAddress || !formData?.postalCode || !formData?.city}
              iconName={isGeocodingAddress ? "Loader" : "MapPin"}
              className="w-full md:w-auto"
            >
              {isGeocodingAddress ? 'Koordinaten werden ermittelt...' : 'Koordinaten automatisch ermitteln'}
            </Button>
            {errors?.geocoding && (
              <p className="text-sm text-error mt-2">{errors?.geocoding}</p>
            )}
            {!formData?.postalCode || !formData?.city ? (
              <p className="text-xs text-muted-foreground mt-2">
                Bitte geben Sie zuerst Postleitzahl und Stadt ein
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Breitengrad (Latitude)"
              type="number"
              step="0.00000001"
              placeholder="z.B. 48.1351"
              name="latitude"
              value={formData?.latitude || ''}
              onChange={handleInputChange}
              leftIcon="MapPin"
            />
            <Input
              label="Längengrad (Longitude)"
              type="number"
              step="0.00000001"
              placeholder="z.B. 11.5820"
              name="longitude"
              value={formData?.longitude || ''}
              onChange={handleInputChange}
              leftIcon="MapPin"
            />
          </div>
          
          <div className="flex items-start gap-2 p-3 bg-primary/10 rounded-lg border border-primary/20 mt-4">
            <Icon name="Info" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <p className="text-xs text-foreground">
              Die Koordinaten werden für die Umkreissuche auf der Partner-Finder-Seite verwendet. 
              Klicken Sie auf &quot;Koordinaten automatisch ermitteln&quot; um diese aus der Adresse zu berechnen.
            </p>
          </div>
        </div>

        <Input
          label="Straße und Hausnummer"
          type="text"
          name="street"
          value={formData?.street}
          onChange={handleInputChange}
          onBlur={() => handleBlur('street')}
          placeholder="z.B. Musterstraße 123"
          error={touched?.street ? errors?.street : ''}
        />

        {/* General Contact Information - Shown on Partner-Finder */}
        <div className="space-y-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Globe" size={20} color="var(--color-primary)" />
            <h3 className="text-sm font-semibold text-foreground">Öffentliche Kontaktdaten</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Diese Daten werden auf der Partner-Finder-Seite öffentlich angezeigt
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Allgemeine E-Mail"
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleInputChange}
              onBlur={() => handleBlur('email')}
              placeholder="info@partner-mueller.de"
              error={touched?.email ? errors?.email : ''}
              description="Wird auf Partner-Finder angezeigt"
              required
            />
            
            <Input
              label="Allgemeine Telefonnummer"
              type="tel"
              name="phone"
              value={formData?.phone}
              onChange={handleInputChange}
              onBlur={() => handleBlur('phone')}
              placeholder="+49 89 123456"
              error={touched?.phone ? errors?.phone : ''}
              description="Wird auf Partner-Finder angezeigt"
              required
            />
          </div>
        </div>

        {/* Contact Person Information - Internal Only */}
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="User" size={20} color="var(--color-muted-foreground)" />
            <h3 className="text-sm font-semibold text-foreground">Ansprechpartner (Intern)</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Diese Daten sind nur für interne Zwecke und werden NICHT auf der Partner-Finder-Seite angezeigt
          </p>
          
          <Input
            label="Name des Ansprechpartners"
            type="text"
            name="contactPerson"
            value={formData?.contactPerson}
            onChange={handleInputChange}
            onBlur={() => handleBlur('contactPerson')}
            placeholder="z.B. Max Mustermann"
            error={touched?.contactPerson ? errors?.contactPerson : ''}
            description="Nur für interne Kommunikation"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="E-Mail Ansprechpartner"
              type="email"
              name="contactEmail"
              value={formData?.contactEmail}
              onChange={handleInputChange}
              onBlur={() => handleBlur('contactEmail')}
              placeholder="max.mustermann@partner-mueller.de"
              error={touched?.contactEmail ? errors?.contactEmail : ''}
              description="Nur für interne Kommunikation"
            />
            
            <Input
              label="Telefon Ansprechpartner"
              type="tel"
              name="contactPhone"
              value={formData?.contactPhone}
              onChange={handleInputChange}
              onBlur={() => handleBlur('contactPhone')}
              placeholder="+49 89 7654321"
              leftIcon="Phone"
              error={touched?.contactPhone ? errors?.contactPhone : ''}
              description="Nur für interne Kommunikation"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Input
            label="Website"
            type="url"
            name="website"
            value={formData?.website}
            onChange={handleInputChange}
            onBlur={() => handleBlur('website')}
            placeholder="https://www.partner-beispiel.de"
            error={touched?.website ? errors?.website : ''}
            required
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="CI Primärfarbe (Hex)"
              type="text"
              name="ciPrimaryColor"
              value={formData?.ciPrimaryColor}
              onChange={handleColorChange}
              onBlur={() => handleBlur('ciPrimaryColor')}
              placeholder="#1E40AF"
              description="z.B. #FF5733"
              error={touched?.ciPrimaryColor ? errors?.ciPrimaryColor : ''}
            />
            
            <Input
              label="CI Sekundärfarbe (Hex)"
              type="text"
              name="ciSecondaryColor"
              value={formData?.ciSecondaryColor}
              onChange={handleColorChange}
              onBlur={() => handleBlur('ciSecondaryColor')}
              placeholder="#3B82F6"
              description="z.B. #33FF57"
              error={touched?.ciSecondaryColor ? errors?.ciSecondaryColor : ''}
            />
          </div>
          
          {formData?.ciPrimaryColor && formData?.ciSecondaryColor && (
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg mt-2">
              <div className="flex items-center gap-2">
                <div 
                  className="w-8 h-8 rounded border-2 border-border"
                  style={{ backgroundColor: formData?.ciPrimaryColor }}
                />
                <span className="text-xs font-mono text-muted-foreground">
                  Primär: {formData?.ciPrimaryColor}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div 
                  className="w-8 h-8 rounded border-2 border-border"
                  style={{ backgroundColor: formData?.ciSecondaryColor }}
                />
                <span className="text-xs font-mono text-muted-foreground">
                  Sekundär: {formData?.ciSecondaryColor}
                </span>
              </div>
            </div>
          )}
          
          <p className="text-xs text-muted-foreground mt-2">
            💡 Tipp: Öffnen Sie die Website und verwenden Sie Browser-Entwicklertools (F12) 
            um die Hex-Codes der CI-Farben zu ermitteln.
          </p>
        </div>

        {/* Gutschein-Konfiguration Section */}
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Gift" size={20} color="var(--color-primary)" />
            <h3 className="text-sm font-semibold text-foreground">Gutschein-Konfiguration</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Gutscheinwert"
              type="number"
              name="voucherValue"
              value={formData?.voucherValue}
              onChange={handleInputChange}
              onBlur={() => handleBlur('voucherValue')}
              placeholder="150"
              min="0"
              step="1"
              description="Wert des Gutscheins (wird auf der Landingpage angezeigt)"
              required
            />
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Währung <span className="text-error">*</span>
              </label>
              <select
                name="voucherCurrency"
                value={formData?.voucherCurrency}
                onChange={handleInputChange}
                onBlur={() => handleBlur('voucherCurrency')}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
                required
              >
                <option value="Euro">Euro (€)</option>
                <option value="Punkte">Punkte</option>
                <option value="Credits">Credits</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Art der Währung für den Gutschein
              </p>
            </div>
          </div>

          {/* Per-year premium configuration */}
          <div className="space-y-3 pt-2 border-t border-border">
            <div className="flex items-center gap-2">
              <Icon name="CalendarCheck" size={16} color="var(--color-primary)" />
              <p className="text-sm font-semibold text-foreground">Prämie pro Jahr (für Headline-Berechnung)</p>
            </div>
            <p className="text-xs text-muted-foreground">
              Legen Sie die Prämie für jedes Jahr individuell fest. Die Summe ergibt die Headline (z.B. 150 + 190 = „bis zu 340 EUR sichern").
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Prämie 2026"
                type="number"
                name="premium2026"
                value={formData?.premium2026}
                onChange={handleInputChange}
                placeholder="150"
                min="0"
                step="1"
                description="Gutscheinwert für das Jahr 2026"
              />
              <Input
                label="Prämie 2027"
                type="number"
                name="premium2027"
                value={formData?.premium2027}
                onChange={handleInputChange}
                placeholder="190"
                min="0"
                step="1"
                description="Gutscheinwert für das Jahr 2027 (0 = kein 2027-Angebot)"
              />
            </div>
            {(Number(formData?.premium2026) > 0 || Number(formData?.premium2027) > 0) && (
              <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg">
                <Icon name="Gift" size={16} color="var(--color-success)" />
                <p className="text-sm text-foreground">
                  Headline: <span className="font-bold">„Jetzt bis zu {Number(formData?.premium2026 || 0) + Number(formData?.premium2027 || 0)} {formData?.voucherCurrency} Gutschein sichern"</span>
                </p>
              </div>
            )}
          </div>
          
          <div className="flex items-start gap-2 p-3 bg-primary/10 rounded-lg border border-primary/20">
            <Icon name="Info" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <p className="text-xs text-foreground">
              Diese Werte werden auf der Partner-Landingpage im Hero-Bereich angezeigt (z.B. "Gutschein" mit Ihrem konfigurierten Betrag)
            </p>
          </div>
        </div>

        {/* Partner Category and Visibility Section */}
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Tag" size={20} color="var(--color-primary)" />
            <h3 className="text-sm font-semibold text-foreground">Partner-Kategorie & Sichtbarkeit</h3>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Partnerkategorie
            </label>
            {loadingCategories ? (
              <div className="flex items-center gap-2 p-3 bg-background border border-border rounded-lg">
                <Icon name="Loader" size={16} color="var(--color-muted-foreground)" className="animate-spin" />
                <span className="text-sm text-muted-foreground">Kategorien werden geladen...</span>
              </div>
            ) : (
              <select
                name="partnerCategoryId"
                value={formData?.partnerCategoryId || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
              >
                {categoryOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Wählen Sie eine Kategorie für diesen Partner (optional)
            </p>
          </div>
          
          <div className="space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="showInPartnerFinder"
                checked={formData?.showInPartnerFinder || false}
                onChange={(e) => setFormData(prev => ({ ...prev, showInPartnerFinder: e?.target?.checked }))}
                className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary/50"
              />
              <span className="text-sm font-medium text-foreground">
                In Partner-Finder anzeigen
              </span>
            </label>
            <p className="text-xs text-muted-foreground ml-7">
              Wenn aktiviert, wird dieser Partner auf der öffentlichen Partner-Finder-Seite angezeigt
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            Logo <span className="text-error">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Hochgeladenes Logo wird nie ersetzt. CI-Extraktion bezieht sich nur auf Farben für die Landingpage.
          </p>
          
          {/* Logo Background Color Input */}
          <div className="mb-4">
            <Input
              label="Logo Hintergrundfarbe (Hex)"
              type="text"
              name="logoBackgroundColor"
              value={formData?.logoBackgroundColor}
              onChange={handleColorChange}
              onBlur={() => handleBlur('logoBackgroundColor')}
              placeholder="#FFFFFF"
              description="Hintergrundfarbe für das Logo (z.B. #000000 für schwarz)"
              error={touched?.logoBackgroundColor ? errors?.logoBackgroundColor : ''}
            />
            {formData?.logoBackgroundColor && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-muted/50 rounded-lg">
                <div 
                  className="w-8 h-8 rounded border-2 border-border"
                  style={{ backgroundColor: formData?.logoBackgroundColor }}
                />
                <span className="text-xs font-mono text-muted-foreground">
                  Vorschau: {formData?.logoBackgroundColor}
                </span>
              </div>
            )}
          </div>

          {logoPreview ? (
            <div className="relative">
              <div 
                className="w-full h-48 rounded-lg overflow-hidden flex items-center justify-center border-2 border-border"
                style={{ backgroundColor: formData?.logoBackgroundColor || '#FFFFFF' }}
              >
                <Image
                  src={logoPreview}
                  alt={formData?.logoAlt || "Vorschau des hochgeladenen Partner-Logos"}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setLogoPreview('');
                  setFormData(prev => ({ ...prev, logo: '', logoAlt: '' }));
                }}
                className="absolute top-2 right-2 p-2 bg-card rounded-lg shadow-card hover:bg-error/10 transition-smooth"
              >
                <Icon name="Trash2" size={18} color="var(--color-error)" />
              </button>
            </div>
          ) : (
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef?.current?.click()}
              className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-smooth cursor-pointer"
            >
              <Icon name="Upload" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground mb-1">
                Logo hochladen oder hierher ziehen
              </p>
              <p className="text-xs text-muted-foreground">
                PNG, JPG, SVG (max. 5MB) - Wird nie automatisch ersetzt
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
            </div>
          )}
          {errors?.logo && (
            <p className="text-sm text-error">{errors?.logo}</p>
          )}
        </div>

        <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
          <div className="flex items-start gap-3">
            <Icon name="Link" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-1">
                Landingpage-URL
              </p>
              <p className="text-sm font-mono text-primary break-all">
                thg.gridbalance.de/{generatedUrl || 'partnerstandort'}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Diese URL wird aus dem Partner-Namen und der Stadt generiert
              </p>
              <p className="text-xs font-medium text-foreground mt-3 mb-1">
                Gutschein-Verifizierungs-URL
              </p>
              <p className="text-sm font-mono text-primary break-all">
                thg.gridbalance.de/{generatedUrl || 'partnerstandort'}/gutscheincheck
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                URL für Kunden zur Gutschein-Validierung
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-3 pt-4 border-t border-border">
        <Button
          type="button"
          variant="outline"
          fullWidth
          onClick={onCancel}
          iconName=""
        >
          Abbrechen
        </Button>
        <Button
          type="submit"
          variant="default"
          fullWidth
          iconName=""
        >
          {dealership ? 'Aktualisieren' : 'Erstellen'}
        </Button>
      </div>
    </form>
  );
};

export default DealershipForm;